/** 转介绍规则的触发条件编辑：保留旧口径，业务决定后可单独保存新版本。 */
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { errMsg } from "./refUtils";

const label = (policy, zh) => {
  if (!policy) return zh ? "沿用现有合同节点" : "Existing contract milestone";
  if (policy.kind === "disabled") return zh ? "暂不自动触发" : "Automatic trigger disabled";
  if (policy.kind === "signed") return zh ? "正式合同签署后" : "After contract signing";
  if (policy.kind === "reservation_deposit") return zh ? "签约且意向预留金到账" : "Signed and reservation deposit received";
  if (policy.kind === "installment") return zh ? `签约且合同第 ${Number(policy.paymentKey?.slice(3))} 期全额到账` : `Signed and installment ${Number(policy.paymentKey?.slice(3))} paid in full`;
  return zh ? "触发规则待核对" : "Trigger needs review";
};

/** @param {object} rule 当前金额规则，触发条件更新时使用版本号防止并发覆盖。 */
export function TriggerPolicyEditor({ rule, zh, canEdit, api, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState("legacy");
  const [period, setPeriod] = useState("pay1");
  const [version, setVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tid = `trigger-${rule.id}`;
  const start = () => {
    setKind(rule.triggerPolicy?.kind || "legacy");
    setPeriod(rule.triggerPolicy?.paymentKey || "pay1");
    setVersion(rule.version);
    setError("");
    setEditing(true);
  };
  const save = async () => {
    const policy = kind === "legacy" ? null : kind === "installment" ? { kind, paymentKey: period } : { kind };
    setBusy(true);
    setError("");
    try {
      await api.put(`/referral/rules/${encodeURIComponent(rule.id)}/trigger`, { expectedVersion: version, triggerPolicy: policy });
      setEditing(false);
      await onSaved();
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };
  return <div className="mt-3 border-t border-border pt-3 space-y-2" data-testid={tid}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground">{zh ? "触发条件" : "Trigger"}：<span className="text-foreground">{label(rule.triggerPolicy, zh)}</span></p>
      {canEdit && !editing && <Button type="button" size="sm" variant="outline" data-testid={`${tid}-edit`} onClick={start}>{zh ? "配置触发" : "Configure"}</Button>}
    </div>
    {editing && <div className="space-y-2">
      {/* 规则卡片本身是两列布局，条件与期次纵向排列可避免较窄桌面屏截断条件名称。 */}
      <div className="grid gap-2">
        <div className="space-y-1"><Label htmlFor={`${tid}-kind`}>{zh ? "触发条件" : "Trigger"}</Label>
          <Select value={kind} onValueChange={setKind} disabled={busy}><SelectTrigger id={`${tid}-kind`} data-testid={`${tid}-kind`}><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="legacy">{zh ? "沿用现有合同节点" : "Existing contract milestone"}</SelectItem>
            <SelectItem value="disabled">{zh ? "暂不自动触发" : "Do not trigger automatically"}</SelectItem>
            <SelectItem value="signed">{zh ? "正式合同签署后" : "After contract signing"}</SelectItem>
            <SelectItem value="reservation_deposit">{zh ? "签约且意向预留金到账" : "Signed and reservation deposit received"}</SelectItem>
            <SelectItem value="installment">{zh ? "签约且指定合同期次全额到账" : "Signed and a selected installment paid in full"}</SelectItem>
          </SelectContent></Select>
        </div>
        {kind === "installment" && <div className="space-y-1"><Label htmlFor={`${tid}-period`}>{zh ? "合同付款期次" : "Contract installment"}</Label>
          <Select value={period} onValueChange={setPeriod} disabled={busy}><SelectTrigger id={`${tid}-period`} data-testid={`${tid}-period`}><SelectValue /></SelectTrigger><SelectContent>{Array.from({ length: 9 }, (_, i) => <SelectItem key={i + 1} value={`pay${i + 1}`}>{zh ? `第 ${i + 1} 期` : `Installment ${i + 1}`}</SelectItem>)}</SelectContent></Select>
        </div>}
      </div>
      {kind === "reservation_deposit" && <p className="text-xs text-muted-foreground">{zh ? "使用财务确认的意向预留金，与合同首期款分开计算。" : "Uses the verified reservation deposit, separately from the first installment."}</p>}
      <p className="text-xs text-muted-foreground">{zh ? "保存后用于新签约记录；已签约记录保留锁定版本。生成待处理记录后由财务办理。" : "Applies to newly signed records; existing signed records retain their locked version. Finance handles any resulting item."}</p>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2"><Button type="button" size="sm" data-testid={`${tid}-save`} disabled={busy} onClick={save}>{zh ? "保存触发条件" : "Save trigger"}</Button><Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>{zh ? "取消" : "Cancel"}</Button></div>
    </div>}
  </div>;
}
